import React, { useState } from "react";

import { Grid } from "@mui/material";

import { Select, TextField } from "@aclymatepackages/atoms";
import { editObjectData } from "@aclymatepackages/array-immutability-helpers";
import { kgsToTons, gallonsToCubicMeters } from "@aclymatepackages/converters";

import {
  buildInitialTransactionInputValueFromSchema,
  isEmissionsInputSchemaEnabled
} from "../../../helpers/components/inputs";

const PurchasedWaterInput = ({
  purchasedWaterQuantity,
  purchasedWaterUnit,
  wastewaterPercentage,
  editPurchasedWaterData,
  disabled
}) => {
  return (
    <Grid container spacing={2} direction="column">
      <Grid item container spacing={2}>
        <Grid item xs={9}>
          <TextField
            label="Quantity"
            value={purchasedWaterQuantity}
            setValue={editPurchasedWaterData("purchasedWaterQuantity")}
            type="number"
            inputProps={{
              min: 0,
              step: 1
            }}
            helperText="Enter the quantity of water"
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={3}>
          <Select
            size="small"
            label="Unit of Measure"
            options={[
              { label: "Cubic Meters", value: "cubicMeters" },
              { label: "US Gallons", value: "gallons" }
            ]}
            value={purchasedWaterUnit}
            editValue={editPurchasedWaterData("purchasedWaterUnit")}
          />
        </Grid>
      </Grid>
      <Grid item>
        <TextField
          label="Wastewater %"
          value={wastewaterPercentage}
          setValue={editPurchasedWaterData("wastewaterPercentage")}
          type="number"
          inputProps={{
            min: 0,
            max: 100,
            step: 1
          }}
          helperText="Optional: Percentage (between 0% and 100%) of purchased water that becomes wastewater"
          disabled={disabled}
        />
      </Grid>
    </Grid>
  );
};

const usePurchasedWaterInput = ({ transaction, onSave }) => {
  const { status } = transaction;
  const inputSchema = [
    { field: "purchasedWaterUnit", defaultValue: "gallons" },
    { field: "purchasedWaterQuantity" },
    { field: "wastewaterPercentage", defaultValue: 0 }
  ];

  const initialTransactionInputValue =
    buildInitialTransactionInputValueFromSchema(transaction, inputSchema);

  const [purchasedWaterData, setPurchasedWaterData] = useState(
    initialTransactionInputValue
  );

  const { purchasedWaterQuantity, purchasedWaterUnit, wastewaterPercentage } =
    purchasedWaterData || {};

  const editPurchasedWaterData = (field) => (value) =>
    editObjectData(setPurchasedWaterData, field, value);

  const calculatePurchasedWaterTonsCo2e = () => {
    const TONS_CO2E_PER_CUBIC_METER = kgsToTons(0.344); //0.344 kg CO2e per cubic meter of water was what was specified by Travis during spec phase
    if (!purchasedWaterQuantity) {
      return 0;
    }

    if (purchasedWaterUnit === "cubicMeters") {
      return purchasedWaterQuantity * TONS_CO2E_PER_CUBIC_METER;
    }

    const cubicMeters = gallonsToCubicMeters(purchasedWaterQuantity);
    return cubicMeters * TONS_CO2E_PER_CUBIC_METER;
  };

  const calculateWastewaterTonsCo2e = () => {
    const TONS_CO2E_PER_CUBIC_METER = kgsToTons(0.17088); //0.17088 kg CO2e per cubic meter of wastewater, from "Water Treatment" tab in GHG spreadsheet
    if (!purchasedWaterQuantity || !wastewaterPercentage) {
      return 0;
    }

    if (purchasedWaterUnit === "cubicMeters") {
      const wastewaterCubicMeters =
        (purchasedWaterQuantity * wastewaterPercentage) / 100;

      return wastewaterCubicMeters * TONS_CO2E_PER_CUBIC_METER;
    }

    const cubicMeters = gallonsToCubicMeters(purchasedWaterQuantity);
    const wastewaterCubicMeters = (cubicMeters * wastewaterPercentage) / 100;

    return wastewaterCubicMeters * TONS_CO2E_PER_CUBIC_METER;
  };

  const purchasedWaterTonsCo2e = calculatePurchasedWaterTonsCo2e();
  const wastewaterTonsCo2e = calculateWastewaterTonsCo2e();
  const tonsCo2e = purchasedWaterTonsCo2e + wastewaterTonsCo2e;

  const onTransactionSave = () =>
    onSave({
      tonsCo2e,
      purchasedWaterTonsCo2e,
      wastewaterTonsCo2e,
      emissionData: purchasedWaterData
    });

  const saveEnabled = isEmissionsInputSchemaEnabled(
    purchasedWaterData,
    inputSchema
  );

  return {
    inputBlock: (
      <PurchasedWaterInput
        editPurchasedWaterData={editPurchasedWaterData}
        {...purchasedWaterData}
        disabled={status === "locked"}
      />
    ),
    saveEnabled,
    onTransactionSave,
    tonsCo2e
  };
};
export default usePurchasedWaterInput;
